"use client";

import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  type ReactNode,
  type ComponentProps,
} from "react";
import { useRouter } from "next/navigation";
import {
  Search,
  MapPin,
  Calendar,
  Users,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Minus,
  Plus,
} from "lucide-react";
import type { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetClose,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { cn, normalizeTr } from "@/lib/utils";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { useIsMobile } from "@/hooks";
import { TURKEY_CITIES } from "@/lib/data/turkey-cities";

const POPULAR_LOCATIONS = ["Kaş", "Bodrum", "Fethiye", "Çeşme", "Kalkan"];

// "bur" → Bursa, Burdur ; "bodrum" → Bodrum ; Türkçe karakter toleranslı
// Şehir adı veya ilçe adı üzerinde eşleşme yapar; maksimum 8 öneri döndürür.
type LocationSuggestion = { label: string; value: string };

function searchLocations(query: string): LocationSuggestion[] {
  const normalized = normalizeTr(query);
  if (!normalized) return [];

  const results: LocationSuggestion[] = [];
  const seen = new Set<string>();

  for (const city of TURKEY_CITIES) {
    if (normalizeTr(city.name).includes(normalized)) {
      if (!seen.has(city.name)) {
        seen.add(city.name);
        results.push({ label: city.name, value: city.name });
      }
    }
    for (const district of city.districts) {
      if (normalizeTr(district).includes(normalized)) {
        const label = `${district}, ${city.name}`;
        if (!seen.has(label)) {
          seen.add(label);
          results.push({ label, value: district });
        }
      }
    }
    if (results.length >= 16) break;
  }

  return results.slice(0, 8);
}

interface SearchBarProps {
  className?: string;
  variant?: "default" | "compact";
  // Anasayfa hero'su: mobilde HEP AÇIK alan kutuları + ayrı "Ara" butonu;
  // masaüstünde hero genişliğini dolduran uzun pill. Yalnız anasayfa hero'da
  // kullanılır; diğer kullanımlar (kapalı mobil pill, içeriği kadar pill) aynı kalır.
  heroOpen?: boolean;
}

export function SearchBar({ className, variant = "default", heroOpen = false }: SearchBarProps) {
  const router = useRouter();
  const isMobile = useIsMobile();
  const [location, setLocation] = useState("");
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [guests, setGuests] = useState(1);
  const [hasPets, setHasPets] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const checkIn = dateRange?.from;
  // react-day-picker aralık modunda İLK tıklamada from=to (aynı gün) yapar. Bu
  // "0 gece" demek → çıkış henüz seçilmemiş sayılır. Eskiden bu hâlde "22 Eki -
  // 22 Eki" görünüyor ve arama checkIn=checkOut ile gidiyordu.
  const checkOut = checkIn && dateRange?.to && dateRange.to > checkIn ? dateRange.to : undefined;
  const formatDay = (d: Date) => format(d, "d MMM", { locale: tr });
  // Tüm varyantların ortak tarih metni; null → her varyant kendi yer tutucusunu yazar.
  const dateLabel = checkIn
    ? `${formatDay(checkIn)} - ${checkOut ? formatDay(checkOut) : "Çıkış seçin"}`
    : null;
  // Bugünden önceki günleri devre dışı bırak (saat sıfırlanmış)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    if (!mobileExpanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!dropdownRef.current) return;

      // Dropdown'ın kendi içine yapılan tıklamaları yok say
      if (dropdownRef.current.contains(target)) return;

      // Radix Popover (takvim, popover'lar) içerikleri Portal ile <body>'ye render
      // edildiği için dropdownRef dışında kalır. Portal içine yapılan tıklamaları
      // "outside" olarak SAYMA — yoksa ay değiştirme/tarih seçme dropdown'ı kapatır.
      if (target instanceof Element) {
        if (
          target.closest("[data-radix-popper-content-wrapper]") ||
          target.closest("[data-radix-popover-content]") ||
          target.closest("[data-slot='popover-content']") ||
          target.closest("[data-slot='calendar']")
        ) {
          return;
        }
      }

      setMobileExpanded(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileExpanded]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (location) params.set("location", location);
    if (checkIn) params.set("checkIn", format(checkIn, "yyyy-MM-dd"));
    if (checkOut) params.set("checkOut", format(checkOut, "yyyy-MM-dd"));
    if (guests > 1) params.set("guests", guests.toString());
    if (hasPets) params.set("pets", "true");
    setMobileExpanded(false);
    router.push(`/listings?${params.toString()}`);
  };

  // Mobil hero: HEP AÇIK tek parça arama kartı (konum / tarihler / misafir
  // satırları). Bir satıra dokununca o alan için alttan açılan panel (bottom
  // sheet) gelir — konum arama, takvim, misafir.
  // Altta ayrı "Ara" butonu. Yalnız anasayfa hero'da
  // (heroOpen) kullanılır; diğer mobil kullanımlar (sonuç sticky header, harita
  // overlay, kaydırınca çıkan sticky pill) kapalı pill kalır → hiçbir yer bozulmaz.
  if (isMobile && heroOpen) {
    const suggestions = searchLocations(location);
    const guestLabel = `${guests} kişi${hasPets ? " + evcil hayvan" : ""}`;
    // Alttan açılan paneller arama kartının üst kenarına kadar uzanır (altta boşluk
    // kalabilir). Mobil hero'da kart header (h-20) + hero üst boşluğu (pt-4) = 6rem
    // aşağıda başlar — ikisinden biri değişirse bu değer de değişmeli.
    const heroSheetClass = "flex h-[calc(100dvh-6rem)] flex-col gap-0 rounded-t-3xl p-0";

    return (
      <div className={cn("w-full", className)}>
        <div className="divide-border/70 divide-y overflow-hidden rounded-3xl bg-white shadow-xl">
          {/* Konum */}
          <Sheet>
            <SheetTrigger asChild>
              <HeroFieldRow
                icon={<MapPin className="h-5 w-5" />}
                label="Konum"
                value={location || "Nereye?"}
                filled={!!location}
              />
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className={heroSheetClass}
              // Otomatik odak alma → mobilde klavye hemen fırlamaz, açılış akıcı
              // olur ve "x'e/seçime ilk dokunuşta kapanmıyor" tutukluğu giderilir.
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <SheetHandle />
              <SheetHeader className="px-5 pt-1 pb-3">
                <SheetTitle className="text-lg">Konum seçin</SheetTitle>
                <SheetDescription className="sr-only">
                  Konaklamak istediğin şehir veya bölgeyi ara ve seç.
                </SheetDescription>
              </SheetHeader>
              <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6">
                <input
                  type="text"
                  placeholder="Şehir veya bölge ara..."
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="focus:border-primary w-full rounded-xl border px-4 py-3 text-[15px] font-medium outline-none"
                />
                {location.trim() ? (
                  <div className="mt-3">
                    {suggestions.length > 0 ? (
                      suggestions.map((s) => (
                        <SheetClose asChild key={s.label}>
                          <button
                            onClick={() => setLocation(s.value)}
                            className="hover:bg-muted flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-[15px]"
                          >
                            <MapPin className="text-muted-foreground h-4 w-4 shrink-0" />
                            {s.label}
                          </button>
                        </SheetClose>
                      ))
                    ) : (
                      <p className="text-muted-foreground py-4 text-center text-sm">
                        Sonuç bulunamadı.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="mt-4">
                    <p className="text-muted-foreground mb-2.5 text-xs font-medium">Popüler</p>
                    <div className="flex flex-wrap gap-2">
                      {POPULAR_LOCATIONS.map((loc) => (
                        <SheetClose asChild key={loc}>
                          <button
                            onClick={() => setLocation(loc)}
                            className="border-border hover:border-primary hover:bg-primary/5 rounded-full border px-4 py-2 text-sm font-medium transition-colors"
                          >
                            {loc}
                          </button>
                        </SheetClose>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>

          {/* Tarih */}
          <Sheet>
            <SheetTrigger asChild>
              <HeroFieldRow
                icon={<Calendar className="h-5 w-5" />}
                label="Tarihler"
                value={dateLabel ?? "Ne zaman?"}
                filled={!!checkIn}
              />
            </SheetTrigger>
            {/* Kaydırılabilir orta bölge: takvim küçük ekranda sığmazsa scroll olur,
                footer (shrink-0) ASLA takvimi ezmez. */}
            <SheetContent side="bottom" className={heroSheetClass}>
              <SheetHandle />
              <SheetHeader className="shrink-0 px-5 pt-1 pb-3">
                <SheetTitle className="text-lg">Tarihler</SheetTitle>
                <SheetDescription className="sr-only">
                  Giriş ve çıkış tarihini takvimden seç.
                </SheetDescription>
              </SheetHeader>
              {/* Takvim sheet genişliğini doldursun (w-full root) → küçük durmasın */}
              <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-2">
                <CalendarComponent
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  disabled={(date) => date < today}
                  locale={tr}
                  numberOfMonths={1}
                  className="w-full [--cell-size:2.2rem]"
                  classNames={{ root: "w-full" }}
                />
              </div>
              <SheetFooter className="shrink-0 flex-row gap-3 border-t px-5 py-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDateRange(undefined)}
                >
                  Temizle
                </Button>
                <SheetClose asChild>
                  <Button className="bg-primary hover:bg-primary/90 flex-1 text-white">
                    Uygula
                  </Button>
                </SheetClose>
              </SheetFooter>
            </SheetContent>
          </Sheet>

          {/* Misafir */}
          <Sheet>
            <SheetTrigger asChild>
              <HeroFieldRow
                icon={<Users className="h-5 w-5" />}
                label="Misafirler"
                value={guestLabel}
                filled
              />
            </SheetTrigger>
            <SheetContent side="bottom" className={heroSheetClass}>
              <SheetHandle />
              <SheetHeader className="px-5 pt-1 pb-3">
                <SheetTitle className="text-lg">Misafirler</SheetTitle>
                <SheetDescription className="sr-only">
                  Misafir sayısını ayarla ve evcil hayvan tercihini belirt.
                </SheetDescription>
              </SheetHeader>
              <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-2">
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-[15px] font-medium">Misafir</p>
                    <p className="text-muted-foreground text-xs">Kaç kişi konaklayacak?</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 rounded-full"
                      onClick={() => setGuests(Math.max(1, guests - 1))}
                      disabled={guests <= 1}
                      aria-label="Misafir sayısını azalt"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-6 text-center text-[15px] font-semibold">{guests}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 rounded-full"
                      onClick={() => setGuests(Math.min(20, guests + 1))}
                      disabled={guests >= 20}
                      aria-label="Misafir sayısını artır"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between border-t py-4">
                  <div>
                    <p className="text-[15px] font-medium">Evcil hayvan</p>
                    <p className="text-muted-foreground text-xs">Yanında evcil hayvan var mı?</p>
                  </div>
                  <Switch
                    checked={hasPets}
                    onCheckedChange={setHasPets}
                    aria-label="Evcil hayvanla geliyorum"
                  />
                </div>
              </div>
              <SheetFooter className="shrink-0 border-t px-5 py-4">
                <SheetClose asChild>
                  <Button className="bg-primary hover:bg-primary/90 w-full text-white">
                    Uygula
                  </Button>
                </SheetClose>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>

        {/* Ayrı "Ara" butonu — kartın altında. Hero zemini ana mavi olduğundan
            mavi buton kaybolurdu; beyaz zemin + mavi yazı (header pill'leriyle aynı dil). */}
        <Button
          onClick={handleSearch}
          className="text-primary hover:text-primary mt-4 h-14 w-full rounded-2xl bg-white text-lg font-semibold shadow-lg hover:bg-white/90"
        >
          Ara
        </Button>
      </div>
    );
  }

  // Mobil: Açılır dropdown
  if (isMobile) {
    return (
      <div ref={dropdownRef} className={cn("relative w-full", className)}>
        <button
          onClick={() => setMobileExpanded(!mobileExpanded)}
          className="flex w-full items-center gap-3 rounded-full border bg-white px-4 py-3 shadow-md transition-shadow hover:shadow-lg"
        >
          <div className="bg-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
            <Search className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-medium">{location || "Nereye?"}</p>
            <p className="text-muted-foreground text-xs">
              {dateLabel ?? "Tarih seçin"}
              {" · "}
              {guests} misafir
            </p>
          </div>
          {mobileExpanded ? (
            <ChevronUp className="text-muted-foreground h-5 w-5" />
          ) : (
            <ChevronDown className="text-muted-foreground h-5 w-5" />
          )}
        </button>

        {mobileExpanded && (
          <div className="absolute top-full right-0 left-0 z-50 mt-2 rounded-2xl border bg-white p-3 shadow-xl">
            <div className="space-y-2">
              <div className="rounded-xl border p-2.5">
                <label className="text-muted-foreground text-xs font-medium">Nereye?</label>
                <input
                  type="text"
                  placeholder="Şehir veya bölge ara..."
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="placeholder:text-muted-foreground mt-1 w-full text-sm font-medium outline-none"
                />
                {location.trim() ? (
                  <div className="mt-1.5 max-h-48 overflow-y-auto">
                    {searchLocations(location).length > 0 ? (
                      searchLocations(location).map((s) => (
                        <button
                          key={s.label}
                          onClick={() => setLocation(s.value)}
                          className="hover:bg-muted block w-full rounded px-2 py-1.5 text-left text-sm"
                        >
                          {s.label}
                        </button>
                      ))
                    ) : (
                      <p className="text-muted-foreground py-2 text-center text-xs">
                        Sonuç bulunamadı.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {POPULAR_LOCATIONS.map((loc) => (
                      <button
                        key={loc}
                        onClick={() => setLocation(loc)}
                        className={cn(
                          "rounded-full border px-2.5 py-1 text-xs transition-colors",
                          location === loc
                            ? "border-primary bg-primary text-white"
                            : "border-border hover:bg-muted"
                        )}
                      >
                        {loc}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-xl border p-2.5">
                <label className="text-muted-foreground text-xs font-medium">Tarih seçin</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="mt-1 grid w-full grid-cols-2 gap-2 text-left">
                      <div className="rounded-lg border px-3 py-2">
                        <p className="text-muted-foreground text-[10px] font-medium">Giriş</p>
                        <p className="text-sm font-medium">
                          {checkIn ? formatDay(checkIn) : "Tarih ekle"}
                        </p>
                      </div>
                      <div className="rounded-lg border px-3 py-2">
                        <p className="text-muted-foreground text-[10px] font-medium">Çıkış</p>
                        <p className="text-sm font-medium">
                          {checkOut ? formatDay(checkOut) : "Tarih ekle"}
                        </p>
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="center">
                    <CalendarComponent
                      mode="range"
                      selected={dateRange}
                      onSelect={setDateRange}
                      disabled={(date) => date < today}
                      locale={tr}
                      numberOfMonths={1}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="rounded-xl border p-2.5">
                <label className="text-muted-foreground text-xs font-medium">Misafirler</label>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-sm font-medium">{guests} misafir</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => setGuests(Math.max(1, guests - 1))}
                      disabled={guests <= 1}
                    >
                      -
                    </Button>
                    <span className="w-6 text-center text-sm font-medium">{guests}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => setGuests(Math.min(20, guests + 1))}
                      disabled={guests >= 20}
                    >
                      +
                    </Button>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between border-t pt-2">
                  <span className="text-sm">Evcil hayvan</span>
                  <Switch
                    checked={hasPets}
                    onCheckedChange={setHasPets}
                    aria-label="Evcil hayvanla geliyorum"
                  />
                </div>
              </div>

              <Button
                onClick={handleSearch}
                className="bg-primary hover:bg-primary/90 w-full rounded-xl text-white"
              >
                Ara
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop compact: Header içi kompakt arama barı
  if (variant === "compact") {
    return (
      <div
        className={cn(
          "inline-flex items-center rounded-full border bg-white shadow-md transition-shadow hover:shadow-lg",
          className
        )}
      >
        <Popover>
          <PopoverTrigger asChild>
            <button className="hover:bg-muted/50 flex items-center gap-2.5 rounded-l-full py-3 pr-5 pl-5 text-sm transition-colors">
              <MapPin className="text-primary h-4 w-4 shrink-0" />
              <span className={cn("font-medium", !location && "text-muted-foreground")}>
                {location || "Nereye?"}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-4" align="start">
            <LocationSearchPanel location={location} setLocation={setLocation} />
          </PopoverContent>
        </Popover>

        <div className="bg-border h-8 w-px" />

        <Popover>
          <PopoverTrigger asChild>
            <button className="hover:bg-muted/50 flex items-center gap-2.5 px-5 py-3 text-sm transition-colors">
              <Calendar className="text-primary h-4 w-4 shrink-0" />
              <span className={cn("font-medium", !checkIn && "text-muted-foreground")}>
                {dateLabel ?? "Ne zaman?"}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="center">
            <div className="p-3">
              <CalendarComponent
                mode="range"
                selected={dateRange}
                onSelect={setDateRange}
                disabled={(date) => date < today}
                locale={tr}
                numberOfMonths={2}
                initialFocus
              />
            </div>
          </PopoverContent>
        </Popover>

        <div className="bg-border h-8 w-px" />

        <Popover>
          <PopoverTrigger asChild>
            <button className="hover:bg-muted/50 flex items-center gap-2.5 py-3 pr-3 pl-5 text-sm transition-colors">
              <Users className="text-primary h-4 w-4 shrink-0" />
              <span className="font-medium">
                {guests} kişi{hasPets ? " +" : ""}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64" align="end">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Misafir</span>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => setGuests(Math.max(1, guests - 1))}
                    disabled={guests <= 1}
                  >
                    -
                  </Button>
                  <span className="w-6 text-center text-sm font-medium">{guests}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => setGuests(Math.min(20, guests + 1))}
                    disabled={guests >= 20}
                  >
                    +
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between border-t pt-4">
                <span className="text-sm font-medium">Evcil hayvan</span>
                <Switch
                  checked={hasPets}
                  onCheckedChange={setHasPets}
                  aria-label="Evcil hayvanla geliyorum"
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <div className="pr-1">
          <Button
            onClick={handleSearch}
            size="icon"
            className="bg-primary hover:bg-primary/90 h-10 w-10 rounded-full text-white"
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Desktop default: Büyük hero arama barı. heroOpen'da alanlar eşit paylaşılır
  // (flex-1) → bar kapsayıcının tüm genişliğini doldurur.
  const fieldWidth = heroOpen && "min-w-0 flex-1";
  // Okunurluk: ikon marka renginde (açık mavi zemin), etiket koyu, değer bir boy
  // büyük; yalnız boş değer (placeholder) gri. Eskiden üçü de gri ve küçüktü →
  // bar silik görünüyordu. Hero'da her şey bir kademe büyük.
  const iconWrapClass = cn(
    "bg-accent text-primary flex shrink-0 items-center justify-center rounded-full",
    heroOpen ? "h-12 w-12" : "h-10 w-10"
  );
  const labelClass = cn(
    "text-foreground leading-tight font-semibold",
    heroOpen ? "text-sm" : "text-[13px]"
  );
  const valueClass = cn("max-w-full truncate font-medium", heroOpen ? "text-base" : "text-sm");
  const dividerClass = cn("bg-border w-px shrink-0", heroOpen ? "h-10" : "h-8");
  return (
    <div
      className={cn(
        heroOpen ? "flex w-full shadow-xl" : "inline-flex border shadow-lg",
        "items-center rounded-full bg-white",
        className
      )}
    >
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={cn(
              "hover:bg-muted/50 flex items-center gap-3 rounded-l-full py-3 pr-7 pl-5 transition-colors lg:pr-8 lg:pl-6",
              fieldWidth,
              // Hero: ikon dairesi barın kenarına eş boşlukla (12px) oturur
              heroOpen && "pl-3 lg:pl-3"
            )}
          >
            <div className={iconWrapClass}>
              <MapPin className="h-5 w-5" />
            </div>
            <div className="flex min-w-0 flex-col items-start">
              <span className={labelClass}>Konum</span>
              <span
                className={cn(valueClass, location ? "text-foreground" : "text-muted-foreground")}
              >
                {location || "Nereye?"}
              </span>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-4" align="start">
          <LocationSearchPanel location={location} setLocation={setLocation} />
        </PopoverContent>
      </Popover>

      <div className={dividerClass} />

      <Popover>
        <PopoverTrigger asChild>
          <button
            className={cn(
              "hover:bg-muted/50 flex items-center gap-3 px-7 py-3 transition-colors lg:px-8",
              fieldWidth
            )}
          >
            <div className={iconWrapClass}>
              <Calendar className="h-5 w-5" />
            </div>
            <div className="flex min-w-0 flex-col items-start">
              <span className={labelClass}>Tarihler</span>
              <span
                className={cn(valueClass, checkIn ? "text-foreground" : "text-muted-foreground")}
              >
                {dateLabel ?? "Ne zaman?"}
              </span>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="center">
          <div className="p-3">
            <CalendarComponent
              mode="range"
              selected={dateRange}
              onSelect={setDateRange}
              disabled={(date) => date < today}
              locale={tr}
              numberOfMonths={2}
              initialFocus
            />
          </div>
        </PopoverContent>
      </Popover>

      <div className={dividerClass} />

      <Popover>
        <PopoverTrigger asChild>
          <button
            className={cn(
              "hover:bg-muted/50 flex items-center gap-3 px-7 py-3 transition-colors lg:px-8",
              fieldWidth
            )}
          >
            <div className={iconWrapClass}>
              <Users className="h-5 w-5" />
            </div>
            <div className="flex min-w-0 flex-col items-start">
              <span className={labelClass}>Misafirler</span>
              <span className={cn(valueClass, "text-foreground")}>
                {guests} kişi{hasPets ? " + evcil hayvan" : ""}
              </span>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64" align="end">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Misafir</span>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  onClick={() => setGuests(Math.max(1, guests - 1))}
                  disabled={guests <= 1}
                >
                  -
                </Button>
                <span className="w-6 text-center text-sm font-medium">{guests}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  onClick={() => setGuests(Math.min(20, guests + 1))}
                  disabled={guests >= 20}
                >
                  +
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between border-t pt-4">
              <span className="text-sm font-medium">Evcil hayvan</span>
              <Switch
                checked={hasPets}
                onCheckedChange={setHasPets}
                aria-label="Evcil hayvanla geliyorum"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <div className={cn("shrink-0", heroOpen ? "pr-2" : "pr-3")}>
        <Button
          onClick={handleSearch}
          aria-label="Ara"
          className={cn(
            "bg-primary hover:bg-primary/90 rounded-full text-white",
            heroOpen ? "h-14 w-14" : "h-12 w-12"
          )}
        >
          <Search className={heroOpen ? "h-6 w-6" : "h-5 w-5"} />
        </Button>
      </div>
    </div>
  );
}

// heroOpen kartındaki tek satır: sol yuvarlak ikon + üstte etiket / altta değer
// + sağda chevron (alttan panel açılacağını gösterir). Masaüstü barla aynı dil:
// ikon marka renginde, etiket koyu, yalnız boş değer gri. SheetTrigger'ın asChild
// çocuğu olduğundan ref ve enjekte edilen prop'ları (onClick, aria-*, data-state)
// button'a forward eder → doğru a11y (aria-expanded/haspopup Radix'ten gelir).
const HeroFieldRow = forwardRef<
  HTMLButtonElement,
  { icon: ReactNode; label: string; value: string; filled: boolean } & ComponentProps<"button">
>(function HeroFieldRow({ icon, label, value, filled, className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "flex w-full items-center gap-3.5 px-4 py-3.5 text-left transition-colors",
        "active:bg-muted/50 data-[state=open]:bg-muted/40",
        className
      )}
      {...props}
    >
      <span className="bg-accent text-primary flex h-11 w-11 shrink-0 items-center justify-center rounded-full">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-foreground block text-[13px] leading-tight font-semibold">
          {label}
        </span>
        <span
          className={cn(
            "mt-0.5 block truncate text-base font-medium",
            filled ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {value}
        </span>
      </span>
      <ChevronRight className="text-muted-foreground/60 h-5 w-5 shrink-0" />
    </button>
  );
});
HeroFieldRow.displayName = "HeroFieldRow";

// Bottom sheet üst tutamağı — modern "alttan panel" görünümü için ince çubuk.
function SheetHandle() {
  return <div className="bg-muted mx-auto mt-3 mb-1 h-1.5 w-10 shrink-0 rounded-full" />;
}

function LocationSearchPanel({
  location,
  setLocation,
}: {
  location: string;
  setLocation: (v: string) => void;
}) {
  const suggestions = searchLocations(location);
  const hasQuery = location.trim().length > 0;

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">Konum seçin</p>
      <input
        type="text"
        placeholder="Şehir veya bölge ara..."
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        className="focus:border-primary w-full rounded-lg border px-3 py-2 text-sm outline-none"
      />
      {hasQuery ? (
        <div className="max-h-56 space-y-0.5 overflow-y-auto">
          {suggestions.length > 0 ? (
            suggestions.map((s) => (
              <button
                key={s.label}
                onClick={() => setLocation(s.value)}
                className="hover:bg-muted block w-full rounded px-2 py-1.5 text-left text-sm"
              >
                {s.label}
              </button>
            ))
          ) : (
            <p className="text-muted-foreground py-2 text-center text-xs">Sonuç bulunamadı.</p>
          )}
        </div>
      ) : (
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs">Popüler</p>
          {POPULAR_LOCATIONS.map((loc) => (
            <button
              key={loc}
              onClick={() => setLocation(loc)}
              className="hover:bg-muted block w-full rounded px-2 py-1.5 text-left text-sm"
            >
              {loc}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
